import { useEffect, useState } from 'react';
import {
  darDeBajaHorario, previewBajaHorario, proximaJornadaDelHorario,
  type BajaPreview, type BajaResultado,
} from '../api/bajaHorario';
import { todayLima } from '../domain/applicationSchema';
import { ddmmaaaa } from '../domain/calendario';
import {
  diaSemanaYFecha, fechaSugeridaDeBaja, sumarDias, type CampaniaRango,
} from '../domain/vigenciaHorario';

const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;
const mensaje = (e: unknown, porDefecto: string): string => (e instanceof Error && e.message) || porDefecto;

/**
 * «Dar de baja desde…» un horario (0304): quita sus jornadas desde una fecha y deja intactas las
 * anteriores. Pide motivo y muestra ANTES de confirmar qué se quita, calculado por el servidor con la
 * misma regla que aplica la baja. Si alguna jornada desde esa fecha tiene gente aprobada, no se puede:
 * se dice quién y en qué fecha, y que primero se retire o reemplace en «Asignaciones».
 */
export default function DarDeBajaHorario({ scheduleId, tienda, campaign, onCancelar, onHecho }: {
  scheduleId: string;
  /** «T10148 — Tottus La Marina». */
  tienda: string;
  campaign: CampaniaRango;
  onCancelar: () => void;
  onHecho: (r: BajaResultado) => void;
}) {
  // «Mañana» en Lima, no en la zona del navegador: la jornada de hoy suele estar en curso.
  const [manana] = useState(() => sumarDias(todayLima(), 1));
  const [desde, setDesde] = useState('');
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState<BajaPreview | null>(null);
  const [calculando, setCalculando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Las dependencias de los efectos van por las fechas y no por el objeto: un objeto nuevo en cada
  // render del padre volvería a pedir la vista previa sin parar.
  const { start_date: inicio, end_date: fin } = campaign;

  // La fecha propuesta: la próxima jornada generada desde mañana.
  useEffect(() => {
    let vivo = true;
    proximaJornadaDelHorario(scheduleId, manana)
      .then((proxima) => { if (vivo) setDesde(fechaSugeridaDeBaja(proxima, manana, { start_date: inicio, end_date: fin })); })
      // Si no se pudo leer, se propone mañana: la vista previa igual valida la fecha en el servidor.
      .catch(() => { if (vivo) setDesde(fechaSugeridaDeBaja(null, manana, { start_date: inicio, end_date: fin })); });
    return () => { vivo = false; };
  }, [scheduleId, manana, inicio, fin]);

  useEffect(() => {
    if (desde === '') return;
    setPreview(null);
    setError(null);
    if (desde < inicio || desde > fin) {
      setCalculando(false);
      setError(`La fecha tiene que estar dentro de la campaña, del ${ddmmaaaa(inicio)} al ${ddmmaaaa(fin)}.`);
      return;
    }
    let vivo = true;
    setCalculando(true);
    previewBajaHorario(scheduleId, desde)
      .then((p) => { if (vivo) setPreview(p); })
      .catch((e: unknown) => { if (vivo) setError(mensaje(e, 'No se pudo calcular qué jornadas se quitan.')); })
      .finally(() => { if (vivo) setCalculando(false); });
    return () => { vivo = false; };
  }, [scheduleId, desde, inicio, fin]);

  const sinMotivo = motivo.trim() === '';
  const puedeConfirmar = preview !== null && !preview.bloqueada && !preview.sinEfecto
    && !sinMotivo && !calculando && !enviando;

  async function confirmar() {
    if (!puedeConfirmar) return;
    setEnviando(true);
    setError(null);
    try {
      onHecho(await darDeBajaHorario(scheduleId, desde, motivo.trim()));
    } catch (e) {
      // El servidor vuelve a mirar: si alguien aprobó a una persona mientras tanto, lo dice acá.
      setError(mensaje(e, 'No se pudo dar de baja el horario.'));
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="baja-horario-titulo"
        className="bg-white rounded p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 id="baja-horario-titulo" className="font-semibold mb-1">Dar de baja el horario de {tienda}</h3>
        <p className="text-sm text-gray-600 mb-3">
          Quita las jornadas de esta tienda desde la fecha que elijas. Las anteriores —también las ya
          trabajadas— no se tocan.
        </p>

        <label className="block text-sm mb-1">Desde
          <input type="date" className="border rounded px-2 py-1 w-full" value={desde}
            min={inicio} max={fin}
            onChange={(e) => setDesde(e.target.value)} />
        </label>
        {desde !== '' && desde < manana && (
          <p className="text-xs text-amber-800 mb-2">
            Esa fecha es hoy o ya pasó: las jornadas sin cubrir desde ese día dejarán de contar como
            programadas.
          </p>
        )}

        <label className="block text-sm mt-2">Motivo <span className="text-gray-500">(obligatorio)</span>
          <textarea className="border rounded px-2 py-1 w-full" rows={2} value={motivo}
            placeholder="Ej.: Bimbo retiró la tienda de la campaña"
            onChange={(e) => setMotivo(e.target.value)} />
        </label>

        <div className="mt-3 text-sm" aria-live="polite">
          {calculando && desde !== '' && <p className="text-gray-500">Calculando qué jornadas se quitan…</p>}

          {preview?.bloqueada && (
            <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-red-800">
              <p className="font-medium">No se puede dar de baja desde el {ddmmaaaa(preview.desde)}.</p>
              {preview.aprobadas.length > 0 && (
                <>
                  <p className="mt-1">Estas jornadas tienen gente aprobada:</p>
                  <ul className="list-disc pl-5">
                    {preview.aprobadas.map((a) => (
                      <li key={`${a.fecha}-${a.persona}`}>{diaSemanaYFecha(a.fecha)} — {a.persona}</li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    Primero retíralas o reemplázalas en «Asignaciones» y vuelve a intentarlo, o elige
                    una fecha posterior.
                  </p>
                </>
              )}
              {preview.conRegistros.length > 0 && (
                <>
                  <p className="mt-1">
                    Estas jornadas ya tienen registros de campo (asistencia, cierre o canjes) y no se borran:
                  </p>
                  <ul className="list-disc pl-5">
                    {preview.conRegistros.map((a) => (
                      <li key={`${a.fecha}-${a.persona}`}>{diaSemanaYFecha(a.fecha)} — {a.persona}</li>
                    ))}
                  </ul>
                  <p className="mt-1">Elige una fecha posterior.</p>
                </>
              )}
            </div>
          )}

          {preview && !preview.bloqueada && preview.sinEfecto && (
            <p className="rounded border border-gray-200 bg-gray-50 p-3 text-gray-700">
              {preview.yaDadoDeBaja
                ? 'Este horario ya está dado de baja: no le queda ningún día.'
                : `Este horario ya termina el ${ddmmaaaa(preview.vigenteHasta)}: no hay nada que dar de baja desde esa fecha.`}
            </p>
          )}

          {preview && !preview.bloqueada && !preview.sinEfecto && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-gray-800">
              {preview.posiciones > 0 ? (
                <p>
                  Se {preview.posiciones === 1 ? 'quita' : 'quitan'}{' '}
                  <strong>{plural(preview.posiciones, 'jornada', 'jornadas')}</strong>:{' '}
                  {preview.fechas.map(diaSemanaYFecha).join(', ')}.
                </p>
              ) : (
                <p>
                  Todavía no hay jornadas generadas desde esa fecha. Igual se cierra el horario, para que
                  «Generar posiciones» no las cree.
                </p>
              )}
              {preview.propuestas > 0 && (
                <p className="mt-1">
                  {plural(preview.propuestas, 'postulación pendiente pasa', 'postulaciones pendientes pasan')} a
                  rechazada{preview.propuestas === 1 ? '' : 's'}.
                </p>
              )}
              <p className="mt-1">
                {preview.quedaSinDias
                  ? 'El horario queda sin días: se verá como «dado de baja».'
                  : `El horario queda vigente hasta el ${ddmmaaaa(preview.vigenteHastaNuevo)}.`}
              </p>
              <p className="mt-1 text-xs text-gray-600">
                Lo que se quita se guarda con el motivo, por si hay que revisarlo después.
              </p>
            </div>
          )}

          {error && <p role="alert" className="mt-2 text-red-600">{error}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4 justify-end">
          {preview && !preview.bloqueada && !preview.sinEfecto && sinMotivo && (
            <span className="text-xs text-gray-500">Escribe el motivo para poder confirmar.</span>
          )}
          <button type="button" className="px-3 py-2 bg-gray-200 rounded" onClick={onCancelar}>Cancelar</button>
          <button type="button" className="px-3 py-2 bg-red-600 text-white rounded disabled:opacity-50"
            disabled={!puedeConfirmar} onClick={() => void confirmar()}>
            {enviando ? 'Dando de baja…' : 'Dar de baja'}
          </button>
        </div>
      </div>
    </div>
  );
}
